import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { dailyItemService } from "../services/authService";

const DeliveryEventModal = ({ deliveryEvent, user, onClose, onSave }) => {
  const [deliveryLog, setDeliveryLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [concernMessage, setConcernMessage] = useState("");
  const [showConcernForm, setShowConcernForm] = useState(false);
  const [adminResponse, setAdminResponse] = useState("");
  const [selectedConcernId, setSelectedConcernId] = useState(null);

  const fetchDeliveryLog = useCallback(async () => {
    try {
      setLoading(true);

      // Create proper date range for the specific day
      const targetDate = new Date(deliveryEvent.deliveryDate);
      const startOfDay = new Date(targetDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(targetDate);
      endOfDay.setHours(23, 59, 59, 999);

      const logs = await dailyItemService.getDeliveryLogs(
        startOfDay.toISOString(),
        endOfDay.toISOString(),
        user.role === "admin" ? deliveryEvent.user?._id : undefined
      );

      // Find the specific log for this daily item and date
      const log = logs.find((l) => {
        const dailyItemMatches =
          l.dailyItem?._id === deliveryEvent.dailyItem._id;

        // Try multiple date comparison methods
        const eventDate = new Date(deliveryEvent.deliveryDate);
        const logDate = new Date(l.deliveryDate || l.date);

        // Compare dates without time
        const eventDateStr = eventDate.toISOString().split("T")[0];
        const logDateStr = logDate.toISOString().split("T")[0];

        const dateMatches = eventDateStr === logDateStr;
        return dailyItemMatches && dateMatches;
      });

      // If no log found and we filtered by user, try without user filter
      if (!log && user.role === "admin" && deliveryEvent.user?._id) {
        const logsWithoutUserFilter = await dailyItemService.getDeliveryLogs(
          startOfDay.toISOString(),
          endOfDay.toISOString()
        );

        const logWithoutUserFilter = logsWithoutUserFilter.find((l) => {
          const dailyItemMatches =
            l.dailyItem?._id === deliveryEvent.dailyItem._id;
          const eventDate = new Date(deliveryEvent.deliveryDate);
          const logDate = new Date(l.deliveryDate || l.date);
          const eventDateStr = eventDate.toISOString().split("T")[0];
          const logDateStr = logDate.toISOString().split("T")[0];
          const dateMatches = eventDateStr === logDateStr;
          return dailyItemMatches && dateMatches;
        });

        if (logWithoutUserFilter) {
          setDeliveryLog(logWithoutUserFilter);
        } else {
          setDeliveryLog(null);
        }
      } else {
        setDeliveryLog(log);
      }
    } catch (error) {
      console.error("Error fetching delivery log:", error);
      toast.error("Failed to fetch delivery details");
    } finally {
      setLoading(false);
    }
  }, [deliveryEvent, user.role]);

  useEffect(() => {
    fetchDeliveryLog();
  }, [fetchDeliveryLog]);

  // Helper function to generate activity logs
  const generateActivityLogs = () => {
    if (!deliveryLog) return [];

    const activities = [];

    // Add delivery creation activity
    activities.push({
      type: "creation",
      timestamp: deliveryLog.createdAt,
      message: "Delivery scheduled",
      icon: "📅",
      color: "#6c757d",
    });

    // Add quantity change activity if exists
    if (deliveryLog.quantityChanged) {
      activities.push({
        type: "quantity_change",
        timestamp: deliveryLog.quantityChangedAt || deliveryLog.updatedAt,
        message: `Quantity changed from ${deliveryLog.originalQuantity} to ${deliveryLog.newQuantity}`,
        icon: "📦",
        color: "#17a2b8",
        reason: deliveryLog.quantityChangeReason,
        changedBy: deliveryLog.quantityChangedBy,
      });
    }

    // Add status updates
    if (deliveryLog.status !== "pending") {
      activities.push({
        type: "status_update",
        timestamp: deliveryLog.deliveredAt || deliveryLog.updatedAt,
        message: `Status updated to: ${deliveryLog.status}`,
        icon: getStatusIcon(deliveryLog.status),
        color: getStatusColor(deliveryLog.status),
        notes: deliveryLog.notes,
      });
    }

    // Add concerns and responses
    if (deliveryLog.concerns && deliveryLog.concerns.length > 0) {
      deliveryLog.concerns.forEach((concern) => {
        // Add concern
        activities.push({
          type: "concern",
          timestamp: concern.raisedAt || concern.createdAt,
          message: concern.message,
          icon: "🚨",
          color: "#dc3545",
          raisedBy: concern.raisedBy,
          concernId: concern._id,
          status: concern.status,
        });

        // Add admin response if exists
        if (concern.adminResponse) {
          activities.push({
            type: "admin_response",
            timestamp: concern.respondedAt,
            message: concern.adminResponse,
            icon: "👨‍💼",
            color: "#28a745",
            respondedBy: concern.respondedBy,
            concernId: concern._id,
          });
        }
      });
    }

    // Sort by timestamp (newest first)
    return activities.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "Unknown time";
    return new Date(timestamp).toLocaleString();
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return "Unknown time";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatActivityDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    return new Date(timestamp).toLocaleDateString();
  };

  const handleStatusUpdate = async (newStatus, notes = "") => {
    if (!deliveryLog) return;

    try {
      await dailyItemService.updateDeliveryStatus(
        deliveryLog._id,
        newStatus,
        notes
      );
      toast.success("Delivery status updated successfully");
      fetchDeliveryLog();
      onSave();
    } catch (error) {
      console.error("Error updating delivery status:", error);
      toast.error("Failed to update delivery status");
    }
  };

  const handleRaiseConcern = async () => {
    if (!deliveryLog || !concernMessage.trim()) return;

    try {
      await dailyItemService.raiseConcern(deliveryLog._id, concernMessage);
      toast.success("Concern raised successfully");
      setConcernMessage("");
      setShowConcernForm(false);
      fetchDeliveryLog();
      onSave();
    } catch (error) {
      console.error("Error raising concern:", error);
      toast.error("Failed to raise concern");
    }
  };

  const handleRespondToConcern = async (concernId) => {
    if (!adminResponse.trim()) return;

    try {
      await dailyItemService.respondToConcern(
        deliveryLog._id,
        concernId,
        adminResponse,
        "resolved"
      );
      toast.success("Response sent successfully");
      setAdminResponse("");
      setSelectedConcernId(null);
      fetchDeliveryLog();
      onSave();
    } catch (error) {
      console.error("Error responding to concern:", error);
      toast.error("Failed to send response");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "#28a745";
      case "missed":
        return "#dc3545";
      case "partial":
        return "#ffc107";
      case "pending":
      default:
        return "#6c757d";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return "✅";
      case "missed":
        return "❌";
      case "partial":
        return "⚠️";
      case "pending":
      default:
        return "⏳";
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth: "600px",
          width: "90%",
          maxHeight: "85vh",
          overflowY: "auto",
          background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
          borderRadius: "20px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <div
          className="modal-header"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px 20px 0 0",
            color: "white",
            padding: "25px 30px",
            borderBottom: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "22px", fontWeight: "600" }}>
            📦 Delivery Details
          </h3>
          <button
            className="close-btn"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ padding: "30px" }}>
          <div className="delivery-info">
            <h4>{deliveryEvent.dailyItem.title}</h4>
            <p>
              <strong>Date:</strong> {deliveryEvent.deliveryDate}
            </p>
            <p>
              <strong>Quantity:</strong>{" "}
              {deliveryLog?.quantityChanged
                ? `${deliveryLog.newQuantity} ${
                    deliveryEvent.dailyItem.unit || ""
                  } (Changed from ${deliveryLog.originalQuantity})`
                : `${deliveryEvent.dailyItem.scheduleQuantity} ${
                    deliveryEvent.dailyItem.unit || ""
                  }`}
            </p>
            {user.role === "admin" && (
              <p>
                <strong>Customer:</strong>{" "}
                {deliveryEvent.user?.username || "N/A"}
              </p>
            )}
            {deliveryEvent.dailyItem.description && (
              <p>
                <strong>Description:</strong>{" "}
                {deliveryEvent.dailyItem.description}
              </p>
            )}
          </div>

          {/* Activity Section */}
          {deliveryLog ? (
            <div className="activity-section" style={{ marginTop: "25px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h5 style={{ margin: 0, color: "#495057" }}>📋 Activity</h5>

                {/* Current Status Badge */}
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    backgroundColor: getStatusColor(deliveryLog.status),
                    color: "white",
                    borderRadius: "15px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                >
                  <span style={{ fontSize: "16px" }}>
                    {getStatusIcon(deliveryLog.status)}
                  </span>
                  <span style={{ textTransform: "capitalize" }}>
                    {deliveryLog.status}
                  </span>
                </div>
              </div>

              {/* Business Controls */}
              {user.role === "admin" && (
                <div
                  style={{
                    marginBottom: "25px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "10px",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <h6 style={{ marginBottom: "15px", color: "#495057" }}>
                    🔧 Business Actions
                  </h6>
                  <div
                    style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  >
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleStatusUpdate("delivered")}
                      disabled={deliveryLog.status === "delivered"}
                      style={{
                        borderRadius: "8px",
                        fontWeight: "500",
                        fontSize: "12px",
                        padding: "6px 12px",
                      }}
                    >
                      ✅ Mark Delivered
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleStatusUpdate("partial")}
                      disabled={deliveryLog.status === "partial"}
                      style={{
                        borderRadius: "8px",
                        fontWeight: "500",
                        fontSize: "12px",
                        padding: "6px 12px",
                      }}
                    >
                      ⚠️ Mark Partial
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleStatusUpdate("missed")}
                      disabled={deliveryLog.status === "missed"}
                      style={{
                        borderRadius: "8px",
                        fontWeight: "500",
                        fontSize: "12px",
                        padding: "6px 12px",
                      }}
                    >
                      ❌ Mark Missed
                    </button>
                  </div>
                </div>
              )}

              {/* Activity Timeline */}
              <div style={{ position: "relative" }}>
                {generateActivityLogs().length > 0 ? (
                  <div style={{ position: "relative" }}>
                    {/* Timeline line */}
                    <div
                      style={{
                        position: "absolute",
                        left: "20px",
                        top: "0",
                        bottom: "0",
                        width: "2px",
                        background:
                          "linear-gradient(to bottom, #667eea, #764ba2)",
                        opacity: "0.3",
                        zIndex: "1",
                      }}
                    />

                    {generateActivityLogs().map((activity, index) => (
                      <div
                        key={index}
                        style={{
                          position: "relative",
                          marginBottom: "20px",
                          paddingLeft: "50px",
                          zIndex: "2",
                        }}
                      >
                        {/* Timeline dot */}
                        <div
                          style={{
                            position: "absolute",
                            left: "12px",
                            top: "5px",
                            width: "16px",
                            height: "16px",
                            backgroundColor: activity.color,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                            zIndex: "3",
                            border: "2px solid white",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          {activity.icon}
                        </div>

                        {/* Activity content */}
                        <div
                          style={{
                            backgroundColor:
                              activity.type === "concern" &&
                              activity.status === "open"
                                ? "#fff3cd"
                                : activity.type === "quantity_change"
                                ? "#e3f2fd"
                                : "white",
                            border: `1px solid ${
                              activity.type === "concern" &&
                              activity.status === "open"
                                ? "#ffeaa7"
                                : activity.type === "quantity_change"
                                ? "#bbdefb"
                                : "#e0e0e0"
                            }`,
                            borderRadius: "10px",
                            padding: "15px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                          }}
                        >
                          {/* Activity header */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "600",
                                color: activity.color,
                                fontSize: "14px",
                              }}
                            >
                              {activity.type === "concern"
                                ? "🚨 Concern Raised"
                                : activity.type === "admin_response"
                                ? "👨‍💼 Business Response"
                                : activity.type === "quantity_change"
                                ? "📦 Quantity Changed"
                                : activity.type === "status_update"
                                ? `${activity.icon} Status Updated`
                                : "📅 Delivery Created"}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6c757d",
                                fontWeight: "500",
                              }}
                            >
                              {formatActivityDate(activity.timestamp)} at{" "}
                              {formatActivityTime(activity.timestamp)}
                            </div>
                          </div>

                          {/* Activity message */}
                          <div
                            style={{
                              color: "#495057",
                              lineHeight: "1.5",
                              marginBottom:
                                activity.reason ||
                                activity.notes ||
                                activity.raisedBy ||
                                activity.respondedBy
                                  ? "10px"
                                  : "0",
                            }}
                          >
                            {activity.message}
                          </div>

                          {/* Additional info based on activity type */}
                          {activity.type === "quantity_change" &&
                            activity.reason && (
                              <div
                                style={{
                                  backgroundColor: "#f1f3f4",
                                  padding: "8px 12px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  color: "#5f6368",
                                  marginBottom: "8px",
                                }}
                              >
                                <strong>Reason:</strong> {activity.reason}
                              </div>
                            )}

                          {activity.notes && (
                            <div
                              style={{
                                backgroundColor: "#f1f3f4",
                                padding: "8px 12px",
                                borderRadius: "6px",
                                fontSize: "13px",
                                color: "#5f6368",
                                marginBottom: "8px",
                              }}
                            >
                              <strong>Notes:</strong> {activity.notes}
                            </div>
                          )}

                          {/* User info */}
                          {(activity.raisedBy ||
                            activity.respondedBy ||
                            activity.changedBy) && (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#6c757d",
                                marginTop: "8px",
                                fontStyle: "italic",
                              }}
                            >
                              by{" "}
                              {activity.raisedBy?.username ||
                                activity.respondedBy?.username ||
                                activity.changedBy?.username ||
                                "System"}
                            </div>
                          )}

                          {/* Business response interface for open concerns */}
                          {user.role === "admin" &&
                            activity.type === "concern" &&
                            activity.status === "open" && (
                              <div
                                style={{
                                  marginTop: "15px",
                                  paddingTop: "15px",
                                  borderTop: "1px solid #e0e0e0",
                                }}
                              >
                                {selectedConcernId === activity.concernId ? (
                                  <div>
                                    <textarea
                                      className="form-control"
                                      rows="3"
                                      placeholder="Type your response here..."
                                      value={adminResponse}
                                      onChange={(e) =>
                                        setAdminResponse(e.target.value)
                                      }
                                      style={{
                                        marginBottom: "10px",
                                        borderRadius: "8px",
                                        border: "2px solid #e0e0e0",
                                        fontSize: "14px",
                                      }}
                                    />
                                    <div>
                                      <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() =>
                                          handleRespondToConcern(
                                            activity.concernId
                                          )
                                        }
                                        disabled={!adminResponse.trim()}
                                        style={{
                                          borderRadius: "6px",
                                          fontWeight: "500",
                                          padding: "6px 12px",
                                        }}
                                      >
                                        📤 Send Response
                                      </button>
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => {
                                          setSelectedConcernId(null);
                                          setAdminResponse("");
                                        }}
                                        style={{
                                          marginLeft: "10px",
                                          borderRadius: "6px",
                                          fontWeight: "500",
                                          padding: "6px 12px",
                                        }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() =>
                                      setSelectedConcernId(activity.concernId)
                                    }
                                    style={{
                                      borderRadius: "6px",
                                      fontWeight: "500",
                                      padding: "6px 12px",
                                    }}
                                  >
                                    💬 Respond to Concern
                                  </button>
                                )}
                              </div>
                            )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#6c757d",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "10px",
                      border: "2px dashed #dee2e6",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "48px",
                        marginBottom: "15px",
                        opacity: "0.5",
                      }}
                    >
                      📋
                    </div>
                    <p style={{ margin: "0", fontSize: "16px" }}>
                      No activity recorded yet
                    </p>
                  </div>
                )}
              </div>

              {/* Raise Concern Form (for users) */}
              {user.role !== "admin" && (
                <div
                  style={{
                    marginTop: "25px",
                    paddingTop: "20px",
                    borderTop: "2px solid #e9ecef",
                  }}
                >
                  {!showConcernForm ? (
                    <button
                      className="btn btn-outline-warning"
                      onClick={() => setShowConcernForm(true)}
                      style={{
                        borderRadius: "10px",
                        fontWeight: "500",
                        padding: "10px 20px",
                        background:
                          "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)",
                        border: "2px solid #ffc107",
                        color: "#856404",
                      }}
                    >
                      🚨 Raise a Concern
                    </button>
                  ) : (
                    <div
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "2px solid #ffc107",
                        borderRadius: "15px",
                        padding: "20px",
                      }}
                    >
                      <h6 style={{ marginBottom: "15px", color: "#856404" }}>
                        🚨 Raise a Concern
                      </h6>
                      <textarea
                        className="form-control"
                        rows="4"
                        placeholder="Describe your concern about this delivery..."
                        value={concernMessage}
                        onChange={(e) => setConcernMessage(e.target.value)}
                        style={{
                          marginBottom: "15px",
                          borderRadius: "10px",
                          border: "2px solid #e0e0e0",
                          fontSize: "14px",
                        }}
                      />
                      <div>
                        <button
                          className="btn btn-warning"
                          onClick={handleRaiseConcern}
                          disabled={!concernMessage.trim()}
                          style={{
                            borderRadius: "8px",
                            fontWeight: "500",
                            padding: "8px 16px",
                          }}
                        >
                          📤 Submit Concern
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowConcernForm(false);
                            setConcernMessage("");
                          }}
                          style={{
                            marginLeft: "10px",
                            borderRadius: "8px",
                            fontWeight: "500",
                            padding: "8px 16px",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#6c757d",
                marginTop: "25px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "15px",
                  opacity: "0.5",
                }}
              >
                📦
              </div>
              <p style={{ margin: "0", fontSize: "16px" }}>
                No delivery log found for this date
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryEventModal;
